import { useEffect, useState } from "react";
import { Send, Trash2, Loader2, Users, User } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const InboxPanel = () => {
  const [msgs, setMsgs] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/messages").then(({ data }) => setMsgs(data.messages)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/admin/messages", { title, body, email });
      toast.success(`${email ? `Message sent to ${email}` : "Message sent to all users"} · phone alert to ${data.push_devices} device(s)`);
      setTitle(""); setBody(""); setEmail(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message for all recipients?")) return;
    await api.delete(`/admin/messages/${id}`).catch(() => {});
    load();
  };

  const input = "w-full bg-white/5 border border-white/15 rounded-lg px-4 py-2.5 outline-none focus:border-brand transition-colors text-sm";

  return (
    <div data-testid="admin-inbox-panel" className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={send} className="bg-card border border-white/10 rounded-2xl p-6 space-y-4">
        <h2 className="font-head text-xl font-bold">Send a message</h2>
        <p className="text-sm text-zinc-400 -mt-2">Appears in every member's app inbox (bell icon) and pops up as a phone alert on devices with alerts turned on. Leave email blank to message all users.</p>
        <input data-testid="msg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Send to one member (email) — optional" className={input} />
        <input data-testid="msg-title" required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className={input} />
        <textarea data-testid="msg-body" required maxLength={2000} rows={5} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message" className={input} />
        <button data-testid="msg-send-btn" disabled={sending}
          className="inline-flex items-center gap-2 bg-brand hover:bg-red-600 font-bold px-6 py-2.5 rounded-lg transition-colors disabled:opacity-60">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {email ? "Send to member" : "Send to all users"}
        </button>
      </form>
      <div className="bg-card border border-white/10 rounded-2xl p-6">
        <h2 className="font-head text-xl font-bold mb-4">Sent messages ({msgs.length})</h2>
        <div className="space-y-3 max-h-[520px] overflow-y-auto">
          {msgs.length === 0 && <p className="text-sm text-zinc-500">No messages sent yet.</p>}
          {msgs.map((m) => (
            <div key={m.id} data-testid={`sent-msg-${m.id}`} className="border border-white/10 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{m.title}</p>
                  <p className="text-sm text-zinc-400 whitespace-pre-line">{m.body}</p>
                  <p className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1">
                    {m.target_email ? <><User size={11} /> {m.target_email}</> : <><Users size={11} /> All users</>} · {new Date(m.created_at).toLocaleString()}
                  </p>
                </div>
                <button data-testid={`delete-msg-${m.id}`} onClick={() => remove(m.id)} aria-label="Delete"
                  className="p-1.5 rounded-md hover:bg-brand/30 text-zinc-400 hover:text-white transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
